import { Instance, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import {
	attachProgramBooks,
	cloneProgram,
	deleteProgram,
	generateLessons,
	invertPublished,
	showProgram,
	updateProgram,
} from '@common/api/programs';
import AcademicLevel from '@common/models/AcademicProducts/AcademicLevel';
import AcademicProduct from '@common/models/AcademicProducts/AcademicProduct';
import RubricGroup from '@common/models/Rubrics/RubricGroup';
import Service from '@common/models/Services/Service';
import { storeReference } from '@common/models/helpers';

import Media from '../Media';
import OneCRate from '../OneCRates/OneCRate';
import ProgramLearningFlow from './ProgramLearningFlow';
import ProgramPurchase from './ProgramPurchase';
import ProgramPurchaseProduct from './ProgramPurchaseProduct';
import { IProgramsModel } from './Programs';

// @TODO-BT : organize types, models, enums ...
export enum Status {
	Draft = 0,
	Active = 1,
}

export type ProgramCreditState = 'current' | 'available' | 'future';

const Program = types
	.model('Program', {
		id: types.identifierNumber,
		is_modular: types.optional(types.boolean, false),
		is_published: types.optional(types.union(types.number, types.boolean, types.null), false),
		name: types.optional(types.string, ''),
		official_name: types.optional(types.string, ''),
		code: types.maybeNull(types.optional(types.string, '')),
		subject: types.maybeNull(types.optional(types.string, '')),
		countLessons: types.optional(types.number, 0),
		service: types.maybe(storeReference(Service)),
		cover_url: types.maybeNull(types.string),
		cover: types.maybeNull(Media),
		isTis: types.optional(types.number, 0),
		discipline_id: types.maybeNull(types.number),
		academicLevel: types.maybeNull(AcademicLevel),
		academicProduct: types.maybeNull(AcademicProduct),
		is_demo: types.maybeNull(types.boolean),
		prompt_id: types.maybeNull(types.number),
		onecRate: types.maybeNull(OneCRate),
		study_direction_tags: types.optional(types.array(types.string), []),
		learning_flow: types.maybeNull(ProgramLearningFlow),
		purchase_products: types.optional(types.array(ProgramPurchaseProduct), []),
		purchase: types.maybeNull(ProgramPurchase),
		assessmentTypes: types.optional(
			types.array(
				types.model({
					id: types.number,
					name: types.string,
				}),
			),
			[],
		),
		rubricUnions: types.optional(types.array(RubricGroup), []),
	})
	.views((self) => ({
		get isProgramPurchased() {
			return Boolean(self.purchase?.is_purchased);
		},
		get programGrade() {
			return self.learning_flow?.grade || self.learning_flow?.entry_grade || null;
		},
		checkFutureGrade(fallbackEntryGrade?: string | null) {
			const grade = self.learning_flow?.gradeToInt(self.learning_flow?.grade)!;
			const eligibleGrade = self.learning_flow
				?.gradeToInt(self.learning_flow?.entry_grade || fallbackEntryGrade)!;

			return grade !== null && eligibleGrade !== null && grade > eligibleGrade;
		},
	}))
	.views((self) => ({
		get isProgramActive() {
			return self.isProgramPurchased && self.purchase?.status.toLowerCase() === 'active';
		},
	}))
	.views((self) => ({
		get paymentProgress(): string | null {
			const paidInstallments = self.purchase?.paid_installments_count;
			const installmentsCount = self.purchase?.installments_count;

			if (paidInstallments === null || paidInstallments === undefined || !installmentsCount) {
				return null;
			}

			return `${paidInstallments}/${installmentsCount}`;
		},
		isFutureGrade(fallbackEntryGrade?: string | null) {
			return self.checkFutureGrade(fallbackEntryGrade);
		},
		getCreditState(fallbackEntryGrade?: string | null): ProgramCreditState {
			if (self.isProgramActive) {
				return 'current';
			}

			return self.checkFutureGrade(fallbackEntryGrade) ? 'future' : 'available';
		},
	}))
	.volatile(() => ({
		loading: false,
	}))
	.actions((self) => ({
		fetch: flow(function* () {
			try {
				self.loading = true;

				const result = yield showProgram(self.id);

				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* () {
			try {
				yield deleteProgram(self.id);
				const parent = getParent<IProgramsModel>(self, 2);
				parent.removeItem(self as IProgramModel);
			} catch (e) {
				console.error(e);
			}
		}),
		save: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield updateProgram(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		attachBooks: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield attachProgramBooks(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		togglePublished: flow(function* () {
			try {
				self.loading = true;
				const result = yield invertPublished(self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		gererateLessons: flow(function* (data) {
			try {
				self.loading = true;
				yield generateLessons(self.id, data);
			} finally {
				self.loading = false;
			}
		}),
		cloneProgram: flow(function* (serviceId: number) {
			yield cloneProgram(self.id, serviceId);
		}),
	}));

export default Program;

export interface IProgramModel extends Instance<typeof Program> {}
export interface IProgramLearningFlowModel extends Instance<typeof ProgramLearningFlow> {}
